import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, valuesOf } from '../skillUsage';

export const shapeMatchCutSkill: CodeoSkill = {
    name: SKILL_NAMES.shapeMatchCut,
    kind: 'codeo',
    description:
        'Cut between two states on a shape they share, so the form persists across the change — ' +
        'the match cut, for making two unrelated things read as the same thing.',
    documentUsesSkill: check(
        (script) =>
            /opacity:\s*0\b/.test(script) &&
            /opacity:\s*1\b/.test(script) &&
            valuesOf(script, 'duration').some((value) => value <= 0.12) &&
            /scale:/.test(script),
    ),
    instructions: `# Shape Match Cut

Two states share a shape in the same position at the same size — a circle that is a clock face and then a coin, a rectangle that is a card and then a doorway. The cut happens while the shape is common to both, so the eye tracks the form across the change and the two states read as continuous.

It is the most economical phase change in the library: it needs no transition at all. The shape does the work, and everything around it simply swaps.

## Markup

The shared shape is one element that never changes. Everything else belongs to one state or the other.

\`\`\`html
<div class="absolute inset-0 flex flex-col justify-center items-start gap-10 p-24">
  <div id="state-a" class="absolute inset-0 flex flex-col items-center gap-10 p-24">
    <div class="text-center" style="font-family: var(--l5-font-0), sans-serif; font-size: 72px; font-weight: 700; color: var(--l5-color-0, #101820)">Twelve hours of work</div>
  </div>
  <div id="state-b" class="absolute inset-0 flex flex-col items-center gap-10 p-24">
    <div class="text-center" style="font-family: var(--l5-font-0), sans-serif; font-size: 72px; font-weight: 700; color: var(--l5-color-0, #101820)">Four dollars of compute</div>
  </div>
  <div id="shared-disc" style="width: 300px; height: 300px; border-radius: 50%; background: var(--l5-color-2, #38bdf8)"></div>
</div>
\`\`\`

## Timeline code

The swap is instantaneous — \`ease: 'none'\` over a couple of frames — and the disc never moves.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const CUT = 2.4;

gsap.set('#state-b', { opacity: 0 });

// The shared shape gets a small nudge across the cut so the eye is already tracking it.
tl.to('#shared-disc', { scale: 1.06, duration: 0.5, ease: 'power2.inOut' }, CUT - 0.5);
tl.to('#state-a', { opacity: 0, duration: 0.05, ease: 'none' }, CUT);
tl.to('#state-b', { opacity: 1, duration: 0.05, ease: 'none' }, CUT);
tl.to('#shared-disc', { scale: 1, duration: 0.6, ease: 'power3.out' }, CUT);

window.__timelines = { main: tl };
\`\`\`

The disc's scale carries *through* the cut — growing before it and settling after — which is what makes the two states read as one continuous shot.

## Adapt

- **Recolour the shape**: letting the disc change colour on the cut, while keeping its geometry, still reads as continuous. Geometry is what the eye tracks, not fill.
- **Rotate through**: a slow rotation spanning the cut is an even stronger continuity cue than scale, especially on a non-circular shape.
- **Two shapes**: a shared rectangle *and* a shared line is fine as long as both are common to the two states. A shape present in only one is what breaks it.
- **Motion match instead**: when there is no shared form but there is shared velocity, use \`${SKILL_NAMES.velocityMatchedCut}\`.
- **Morph instead**: when the shape should visibly become something else rather than persist unchanged, use \`${SKILL_NAMES.facetMorphShape}\` or \`${SKILL_NAMES.elementMorphHandoff}\`.
- **Three states**: chaining match cuts on one persistent shape is a strong spine for a longer scene — see \`${SKILL_NAMES.morphChainSpine}\`.

## Pitfalls

- The shared element must sit *outside* both state containers in the DOM, or it is hidden along with the state it belongs to.
- It must be identical in position and size across the cut. Even a few pixels of drift breaks the continuity and the cut reads as a mistake.
- Keep the swap under about 0.08s. A crossfade between the two states destroys the effect — the whole point is that there is no transition.
- Don't move the shape *during* the swap. The motion must span the cut smoothly, not happen at it.
- The two states must be genuinely worth connecting. A match cut asserts a relationship, and on unrelated content it just reads as a jump.
- The disc is \`scale\`-tweened, so it must not carry a CSS \`transform\`; centre it with \`left\`/\`top\`.
- Both states' text must clear the shape. Copy running under the shared element is unreadable in both.`,
};
